import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Clock, Tag } from "lucide-react";
import { INTENT_PAGES, getIntentPage } from "@/lib/intent-pages";
import { getService } from "@/lib/services-data";
import { Disclaimer } from "@/components/site/Disclaimer";
import { NonReservedNotice } from "@/components/site/NonReservedNotice";

const BASE = "https://nkadimengdannylcs.com";

export const Route = createFileRoute("/legal-help/$topic")({
  loader: ({ params }) => {
    const page = getIntentPage(params.topic);
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData, params }) => {
    const page = loaderData?.page;
    const url = `${BASE}/legal-help/${params.topic}`;
    if (!page) return { meta: [{ title: "Legal help" }] };
    return {
      meta: [
        { title: page.metaTitle },
        { name: "description", content: page.metaDescription },
        { property: "og:title", content: page.metaTitle },
        { property: "og:description", content: page.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: page.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.h1,
            description: page.metaDescription,
            areaServed: "ZA",
            url,
            provider: {
              "@type": "Organization",
              name: "Nkadimeng Danny Legal & Compliance Support",
              url: BASE,
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-narrow py-16">
      <h1 className="font-display text-2xl text-navy">Page not found</h1>
      <Link to="/services" className="mt-3 inline-block text-navy underline">
        Browse our services
      </Link>
    </div>
  ),
  component: IntentPageView,
});

function IntentPageView() {
  const { page } = Route.useLoaderData();
  const service = getService(page.serviceSlug);
  const others = INTENT_PAGES.filter((p) => p.slug !== page.slug).slice(0, 3);

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-14 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-3xl text-navy md:text-4xl">{page.h1}</h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{page.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5">
              <Clock className="h-4 w-4 text-gold" /> {page.turnaround}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5">
              <Tag className="h-4 w-4 text-gold" /> {page.priceHint}
            </span>
          </div>
          <Link
            to={page.cta.to}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-navy-foreground"
          >
            {page.cta.label} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="container-narrow py-12">
        <h2 className="font-display text-2xl text-navy">What you receive</h2>
        <ul className="mt-4 space-y-3">
          {page.deliverables.map((d) => (
            <li key={d} className="flex items-start gap-2 text-foreground">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span>{d}</span>
            </li>
          ))}
        </ul>

        <h2 className="mt-12 font-display text-2xl text-navy">Common questions</h2>
        <div className="mt-4 space-y-6">
          {page.faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-navy">{f.q}</h3>
              <p className="mt-1 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <NonReservedNotice />
        </div>

        {service ? (
          <p className="mt-8 text-sm text-muted-foreground">
            This page is part of our{" "}
            <Link to="/services/$slug" params={{ slug: service.slug }} className="text-navy underline">
              {service.title}
            </Link>{" "}
            service.
          </p>
        ) : null}

        <h2 className="mt-12 font-display text-2xl text-navy">Other things we help with</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {others.map((o) => (
            <Link
              key={o.slug}
              to="/legal-help/$topic"
              params={{ topic: o.slug }}
              className="rounded-lg border border-border p-4 transition hover:border-gold"
            >
              <p className="font-medium text-navy">{o.h1}</p>
              <p className="mt-1 text-sm text-muted-foreground">{o.intent}</p>
            </Link>
          ))}
        </div>

        <div className="mt-12">
          <Disclaimer />
        </div>
      </section>
    </>
  );
}
